import { useCallback, useState, type HTMLAttributes } from "react";
import ArrowForward from "@mui/icons-material/ArrowForward";
import Close from "@mui/icons-material/Close";
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Form, Formik } from "formik";
import { Trans, useTranslation } from "react-i18next";

import { SubmissionButton } from "@thalamos/frontend";
import type {
  IncidentWorkItemIdUnbranded,
  UserUnbranded,
} from "@thalamos/vision-common";
import { isCurrentUserSupervisor, tk } from "@thalamos/vision-common";

import type { SearchUsersFn } from "../../api/user/createUserSearch.js";
import OfficerIconOne from "../../assets/img/officer_icon_1.svg";
import OfficerIconTwo from "../../assets/img/officer_icon_2.svg";
import { PopupDialog } from "../../components/Dialog/PopupDialog.js";
import { Typeahead } from "../../components/Typeahead/Typeahead.js";
import { useUser } from "../../Context/UserContext.js";

export interface InitiateTransferModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (email: string) => Promise<void>;
  incidentWorkItemId: IncidentWorkItemIdUnbranded | undefined;
  searchUsers: SearchUsersFn;
  isAdmin?: boolean;
}

export function InitiateTransferModal({
  open,
  onClose,
  onConfirm,
  incidentWorkItemId,
  searchUsers,
  isAdmin,
}: InitiateTransferModalProps) {
  const { user } = useUser();
  // selectedEmail: the actual email address to pass to onConfirm
  const [selectedEmail, setSelectedEmail] = useState<string>("");
  // displayValue: what is shown in the typeahead input (may include shoulder number label)
  const [displayValue, setDisplayValue] = useState<string>("");
  const [options, setOptions] = useState<UserUnbranded[]>([]);

  const { t } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  // Returns the plain email — used as the input value after selection and for accessible option names.
  const getOptionLabelFn = useCallback((option: string | UserUnbranded) => {
    if (typeof option === "string") return option;
    return option.email;
  }, []);

  // Client-side filter: match on email, name, or shoulder number so that server results
  // found by shoulder number are not hidden by MUI's default email-only filter.
  // Only `inputValue` is needed from MUI's FilterOptionsState; `getOptionLabel` is unused.
  const filterOptionsFn = useCallback(
    (
      opts: UserUnbranded[],
      state: {
        inputValue: string;
        getOptionLabel: (option: UserUnbranded) => string;
      },
    ): UserUnbranded[] => {
      const query = state.inputValue.toLowerCase();
      if (!query) return opts;
      return opts.filter(
        (opt) =>
          opt.email.toLowerCase().includes(query) ||
          opt.name.toLowerCase().includes(query) ||
          (opt.shoulderNumber?.toLowerCase().includes(query) ?? false),
      );
    },
    [],
  );

  // Two-row option renderer: bold email on top, smaller name / name (shoulder_number) below.
  const renderOptionFn = useCallback(
    (props: HTMLAttributes<HTMLLIElement>, option: UserUnbranded) => {
      const shoulderNumber =
        option.shoulderNumber &&
        !option.name.toLowerCase().includes(option.shoulderNumber.toLowerCase())
          ? option.shoulderNumber
          : undefined;

      const subLabel = shoulderNumber
        ? `${option.name} (${shoulderNumber})`
        : option.name;

      return (
        <li {...props} key={option.email}>
          <Box>
            <Typography
              component="div"
              sx={{ fontWeight: "bold", fontSize: "0.875rem" }}
            >
              {option.email}
            </Typography>
            <Typography
              component="div"
              sx={{ fontSize: "0.75rem", color: "text.secondary" }}
            >
              {subLabel}
            </Typography>
          </Box>
        </li>
      );
    },
    [],
  );

  const handleConfirm = async () => {
    // TODO - think about how we want to handle validation
    if (selectedEmail) {
      await onConfirm(selectedEmail.trim());
    }
  };

  const handleInputChange = async (inputValue: string | null) => {
    const rawValue = inputValue ?? "";
    setDisplayValue(rawValue);

    // If the typed/selected value matches a known option's label (e.g. "email (shoulder)"),
    // extract the plain email from that option so onConfirm always receives a bare email.
    const matchingOption = options.find(
      (opt) => getOptionLabelFn(opt) === rawValue,
    );

    if (matchingOption) {
      setSelectedEmail(matchingOption.email);
      // No need to re-search when user just picked from the dropdown
      return;
    }

    setSelectedEmail(rawValue);

    if (rawValue && rawValue.length > 2) {
      const results = (await searchUsers(rawValue, 10, 0).unwrapOr([])).filter(
        (searchResult) => {
          // If user is a supervisor, don't filter out their own email
          if (user && isCurrentUserSupervisor(user)) {
            return true;
          }
          return searchResult.email !== user?.email;
        },
      );

      setOptions(results);
    } else {
      setOptions([]);
    }
  };

  return (
    <PopupDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={fullScreen}
      disableDialogRootPadding={true}
    >
      <DialogTitle sx={{ fontSize: "1.250rem", fontWeight: "bold" }}>
        {t("transfer.initiateTransfer.title")}
        <IconButton
          aria-label="close"
          onClick={onClose}
          edge="end"
          sx={{
            position: "absolute",
            right: 15,
            top: 8,
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          mt: 1,
        }}
      >
        <img src={OfficerIconOne} alt="Officer 1" />
        <ArrowForward sx={{ fontSize: "1.5rem", color: "text.secondary" }} />
        <img src={OfficerIconTwo} alt="Officer 2" />
      </Box>
      <DialogContent>
        <Typography
          component="div"
          sx={{ color: "primary", fontSize: "0.875rem" }}
          gutterBottom
        >
          <Typography
            sx={{ color: "primary", fontSize: "0.875rem" }}
            gutterBottom
          >
            {t("transfer.initiateTransfer.transferIncidentWorkItemId")}
          </Typography>
          <Typography
            sx={{ color: "primary", fontSize: "0.875rem" }}
            gutterBottom
          >
            {incidentWorkItemId}
          </Typography>
          <Trans
            i18nKey="transfer.initiateTransfer.confirmedTransfer"
            components={{ bold: <strong /> }}
          />
        </Typography>
        <Typography
          sx={{ color: "primary", fontSize: "0.875rem", mb: 2 }}
          gutterBottom
        >
          {isAdmin
            ? t("transfer.initiateTransfer.adminUntilTransferAccepted")
            : t("transfer.initiateTransfer.untilTransferAccepted")}
        </Typography>

        <Formik
          initialValues={{ email: selectedEmail }} // TODO should the submission buttons use this instead
          onSubmit={(_values) => {}}
        >
          {() => (
            <Form>
              <Typeahead
                inputValue={displayValue}
                placeholder={{
                  translationKey: tk(
                    "transfer.initiateTransfer.enterEmailAddress",
                  ),
                }}
                name={"transfer"}
                label={undefined}
                fieldName="transfer"
                onInputChange={(event) => void handleInputChange(event)}
                options={options}
                optionsKey="email"
                getOptionLabel={getOptionLabelFn}
                filterOptions={filterOptionsFn}
                renderOption={renderOptionFn}
              />
            </Form>
          )}
        </Formik>
      </DialogContent>
      <DialogActions
        sx={{
          display: "flex",
          justifyContent: "space-between",
          px: 3,
        }}
      >
        <Button onClick={onClose} color="error" variant="outlined">
          {t("common.close")}
        </Button>
        <SubmissionButton
          onClick={handleConfirm}
          color="primary"
          variant="contained"
          disabled={!selectedEmail}
        >
          {t("transfer.initiateTransfer.confirm")}
        </SubmissionButton>
      </DialogActions>
    </PopupDialog>
  );
}
